'use client';

import { useEffect, useState } from 'react';
import { apiFetch } from '@/platform/lib/api';
import { useAuth } from './useAuth';

/**
 * `/saas/tenants/mine` is the ops registry. Product slug Hosting rewrites `/api`
 * to incubator/tenant — probing there only yields noisy 404s.
 */
export function shouldProbeOpsTenantMe(hostname: string): boolean {
  const h = hostname.trim().toLowerCase().replace(/\.$/, '');
  if (!h) return false;
  if (h === 'ops.communiosocial.com') return true;
  // Local/dev product shell may still hit an ops-backed API.
  if (h === 'localhost' || h.endsWith('.localhost')) return true;
  return false;
}

/**
 * True when the signed-in user owns a SaaS community (ops registry) or can manage
 * marketplace on the current soft-middle host. Used to hide host-only avatar links.
 */
export function useIsCommunityHost(enabled: boolean): { ready: boolean; isHost: boolean } {
  const { user, isLoading } = useAuth();
  const [ready, setReady] = useState(!enabled);
  const [isHost, setIsHost] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setReady(true);
      setIsHost(false);
      return;
    }
    if (isLoading) return;
    if (!user) {
      setReady(true);
      setIsHost(false);
      return;
    }
    let cancelled = false;
    setReady(false);
    (async () => {
      const host = typeof window !== 'undefined' ? window.location.hostname : '';
      if (shouldProbeOpsTenantMe(host)) {
        try {
          const owned = await apiFetch<{ tenants?: unknown[] }>('/saas/tenants/mine');
          if (!cancelled) {
            setIsHost((owned.tenants?.length || 0) > 0);
            setReady(true);
          }
          return;
        } catch {
          /* fall through to marketplace status */
        }
      }
      try {
        const st = await apiFetch<{ canManage?: boolean }>('/saas/me/marketplace/status');
        if (!cancelled) {
          setIsHost(!!st.canManage);
          setReady(true);
        }
      } catch {
        if (!cancelled) {
          setIsHost(false);
          setReady(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, isLoading, user]);

  return { ready, isHost };
}
